import { useState } from 'react';
import type { AvailableUpdate } from '../update';

interface Props {
  update: AvailableUpdate;
  /** 원격 세션이 진행 중인지 — 진행 중이면 설치를 **제안하지 않는다**. */
  sessionActive: boolean;
  /** 사용자가 배너를 닫음(이번 실행 동안 다시 띄우지 않는다). */
  onDismiss: () => void;
}

/**
 * 새 버전 알림 배너(설계 §5).
 *
 * ⚠️ **원격 세션 중에는 설치 버튼을 내보내지 않는다.** 설치는 앱 재시작을 뜻하고, 재시작은 진행 중인
 * 원격지원을 그 자리에서 끊는다. "업데이트가 있다"는 사실만 알리고 설치는 세션이 끝난 뒤로 미룬다.
 */
export function UpdateBanner({ update, sessionActive, onDismiss }: Props) {
  const [installing, setInstalling] = useState(false);
  const [failed, setFailed] = useState('');

  const install = async () => {
    setInstalling(true);
    setFailed('');
    try {
      await update.install(); // 성공하면 재시작되므로 이 아래로 돌아오지 않는다
    } catch (e) {
      // 여기 도달했다면 설치가 실패한 것이다. 사용자가 다시 시도하거나 나중으로 미룰 수 있게 남긴다.
      setFailed((e as Error).message || '업데이트 설치에 실패했습니다.');
      setInstalling(false);
    }
  };

  return (
    <div className="update-banner">
      <div className="update-text">
        <strong>새 버전 {update.version}</strong>
        {update.notes && <span className="update-notes">{update.notes}</span>}
        {sessionActive && (
          <span className="update-hint">원격 세션이 끝난 뒤에 설치할 수 있습니다.</span>
        )}
        {/* 설치가 시작되면 앱 창이 통째로 사라졌다가 새 버전으로 다시 열린다.
            미리 말해 주지 않으면 "업데이트를 눌렀더니 앱이 꺼졌다" 로 보인다. */}
        {installing && (
          <span className="update-hint">
            앱이 잠시 닫혔다가 새 버전으로 다시 열립니다. 그대로 기다려 주세요.
          </span>
        )}
        {failed && <span className="update-error">{failed}</span>}
      </div>
      <div className="update-actions">
        {!sessionActive && (
          <button onClick={install} disabled={installing}>
            {installing ? '설치 중…' : '지금 업데이트'}
          </button>
        )}
        <button className="ghost" onClick={onDismiss} disabled={installing}>
          나중에
        </button>
      </div>
    </div>
  );
}
